import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Card, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { Custom_Price_List_Packaging_Insert_Input } from '@kheops/graphql';
import { currentContextAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import { PageWrapper } from '../../common/components/PageWrapper';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import TopAppBar from '../../common/components/TopAppBar';
import useCustomPriceListFormDefaultValues from './hooks/useCustomPriceListFormDefaultValues';
import useUpsertCustomPriceListMutation, { UpsertCustomPriceListMutationVariables } from './hooks/useUpsertCustomPriceListMutation';
import { DraftCustomPriceList } from './state';
import CustomPriceListInfoForm from './CustomPriceListInfoForm';
import CustomPriceListProductsForm from './CustomPriceListProductsForm';
import CustomPriceListFormButtons from './CustomPriceListFormButtons';
import CustomPriceListFormHeader from './hooks/CustomPriceListFormHeader';
import CustomPriceListInfoFormDrawerButton from './CustomPriceListInfoFormDrawerButton';

export default function CustomPriceListForm(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['price', 'catalogs']);
  const { companyId } = useAtomValue(currentContextAtom);
  const contractId = searchParams.get('contractId') || undefined;
  const customPriceListId = searchParams.get('customPriceListId') || undefined;
  const copyCustomPriceListId = searchParams.get('copyCustomPriceListId') || undefined;
  const defaultValues = useCustomPriceListFormDefaultValues({
    customPriceListId: customPriceListId || copyCustomPriceListId,
    copy: !!copyCustomPriceListId,
    contractId,
  });
  const form = useForm<DraftCustomPriceList>({ defaultValues, mode: 'onChange' });
  const {
    handleSubmit,
    reset,
    formState,
    watch,
  } = form;
  const { upsertCustomPriceListMutation } = useUpsertCustomPriceListMutation();
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [shouldDisplayProductsFormMobile, setShouldDisplayProductsFormMobile] = useState(!!(customPriceListId || copyCustomPriceListId));
  const [customPriceListContracts, setCustomPriceListContracts] = useState<string[]>(defaultValues.contracts);
  const [customPriceListName, setCustomPriceListName] = useState<string>(defaultValues.name);
  const [customPriceListFormName, customPriceListFormContracts] = [watch('name'), watch('contracts')];

  useEffect(() => {
    if (shouldDisplayProductsFormMobile && !customPriceListName) {
      setCustomPriceListContracts(customPriceListFormContracts);
      setCustomPriceListName(customPriceListFormName);
    }
  }, [shouldDisplayProductsFormMobile, customPriceListFormName]);

  const onSubmit = useCallback(async (draftCustomPriceList: DraftCustomPriceList): Promise<void> => {
    const upsertCustomPriceListMutationVariables = {} as UpsertCustomPriceListMutationVariables;
    const customPriceListPackagingsPayload = Object.entries(draftCustomPriceList.prices).reduce((acc, [packagingSku, draftPrice]) => {
      if (draftPrice.discount) {
        acc.push({
          packaging_sku: parseInt(packagingSku, 10),
          discount: draftPrice.discount / 100,
          company_id: companyId,
        });
      }

      return acc;
    }, [] as Custom_Price_List_Packaging_Insert_Input[]);
    let successLabel: string;

    upsertCustomPriceListMutationVariables.insertCustomPriceListVariables = {
      custom_price_list: {
        name: draftCustomPriceList.name,
        created_at: draftCustomPriceList.created_at,
        reference_id: draftCustomPriceList.reference_id,
        company_id: companyId,
        custom_price_list_packagings: {
          data: customPriceListPackagingsPayload,
        },
      },
    };

    if (!customPriceListId) {
      if (!contractId) {
        upsertCustomPriceListMutationVariables.relatedContractIds = draftCustomPriceList.contracts;
      }

      successLabel = 'price:custom_price_list_created';
    } else {
      upsertCustomPriceListMutationVariables.relatedContractIds = draftCustomPriceList.contracts;
      upsertCustomPriceListMutationVariables.deletedContractIds = defaultValues.contracts.filter((value) => !draftCustomPriceList.contracts.includes(value));

      successLabel = 'price:custom_price_list_updated';
    }

    const newCustomPriceListId = await upsertCustomPriceListMutation(upsertCustomPriceListMutationVariables);

    if (location.state?.origin) {
      navigate(
        {
          pathname: location.state.origin,
          search: newCustomPriceListId ? `?${createSearchParams({ newCustomPriceListId })}` : undefined,
        },
        {
          state: { ...location.state, newCustomPriceListId, newCustomPriceListName: draftCustomPriceList.name },
        },
      );
    } else {
      navigate(RoutePaths.COMPANY_CUSTOM_PRICE_LISTS);
    }

    setCommonSnackbarProps({
      label: t(successLabel),
      snackbarProps: {
        open: true,
      },
    });
  }, [customPriceListId, defaultValues]);

  useEffect((): void => {
    reset(defaultValues);
  }, [defaultValues]);

  const formComponent = useMemo(() => {
    if (!isDownLg) {
      return (
        <Grid
          container
          sx={{
            height: 'calc(100vh - 168px)',
          }}
          columnSpacing={2}
        >
          <Grid size={{ xs: 0, lg: 4 }} sx={{ display: { xs: 'none', lg: 'flex' } }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
              <CustomPriceListFormHeader edit={!!customPriceListId} />
              <CustomPriceListInfoForm />
              <CustomPriceListFormButtons copy={!!copyCustomPriceListId} edit={!!customPriceListId} fullWidth />
            </Card>
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }} sx={{ maxHeight: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% + 24px)',
                pb: {
                  lg: 0,
                },
                p: 0,
                gap: 3,
                borderRadius: 0,
              }}
            >
              <CustomPriceListProductsForm />
            </Box>
          </Grid>
        </Grid>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 164px)',
          mt: 6.5,
        }}
      >
        {shouldDisplayProductsFormMobile
          ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <CustomPriceListProductsForm />
              <CustomPriceListFormButtons
                parentBoxSx={{
                  position: 'fixed',
                  bottom: 0,
                  backgroundColor: 'surface.main',
                  width: 'calc(100vw - 32px)',
                  left: 0,
                  mx: 2,
                }}
                fullWidth
                shouldDisplayCancelButton={false}
                copy={!!copyCustomPriceListId}
                edit={!!customPriceListId}
              />
            </Box>
          )
          : (
            <Box sx={{ display: 'flex', flexDirection: 'column', px: 2 }}>
              <CustomPriceListInfoForm />
              <Button
                variant="contained"
                disabled={!formState.isValid}
                onClick={() => setShouldDisplayProductsFormMobile(true)}
                startIcon={<AddIcon />}
                sx={{
                  mt: 4,
                }}
              >
                {t('catalogs:add_packagings')}
              </Button>
            </Box>
          )}
      </Box>
    );
  }, [isDownLg, shouldDisplayProductsFormMobile, formState]);

  return (
    <PageWrapper
      sx={{
        px: {
          xs: 2,
          lg: 0,
        },
      }}
    >
      <FormProvider {...form}>
        <TopAppBar
          configuration="small"
          backTo={RoutePaths.COMPANY_CUSTOM_PRICE_LISTS}
          shouldCheckHistory={false}
          title={shouldDisplayProductsFormMobile ? customPriceListName : t('price:create_custom_price_list')}
          trailingAction={
            shouldDisplayProductsFormMobile && (
              <CustomPriceListInfoFormDrawerButton
                name={customPriceListName}
                contracts={customPriceListContracts}
                setContracts={setCustomPriceListContracts}
                setName={setCustomPriceListName}
                size="large"
              />
            )
          }
          sx={{
            position: 'fixed',
          }}
        />
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {formComponent}
        </Box>
      </FormProvider>
    </PageWrapper>
  );
}
